import React, { useEffect, useMemo, useRef, useState } from "react";
import { Camera, Radio, Unplug, Zap } from "lucide-react";
import { Button } from "./ui/button";
import { useLiveScale } from "../hooks/useLiveScale";
import { useAuth, hasRole } from "../context/AuthContext";
import {
  acquireWeighbridgeStream,
  isCloudScaleCaptureBlocked,
  isLocalWeighbridgeHost,
  subscribeLivePreviews,
} from "../lib/liveScale";
import { formatKg, kgToQtl } from "../lib/weightUnits";

const IDLE_MS = 60_000;

function b64Src(b64) {
  if (!b64) return "";
  if (String(b64).startsWith("data:")) return b64;
  return `data:image/jpeg;base64,${b64}`;
}

function StatusPill({ label, tone }) {
  const cls =
    tone === "green"
      ? "bg-emerald-600/15 text-emerald-700 dark:text-emerald-400 border-emerald-600/30"
      : tone === "blue"
        ? "bg-sky-600/15 text-sky-700 dark:text-sky-400 border-sky-600/30"
        : tone === "amber"
          ? "bg-amber-500/15 text-amber-800 dark:text-amber-300 border-amber-500/40"
          : "bg-red-600/15 text-red-700 dark:text-red-400 border-red-600/30";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${cls}`}>
      {label}
    </span>
  );
}

/**
 * Live mill-scale HUD for Gate Inward / Gross / Tare.
 * Localhost streams continuously; cloud tabs stay in standby until Connect.
 */
export default function LiveWeighbridgeHUD({
  className = "",
  testId = "live-weighbridge-hud",
  showCapture = false,
  capturing = false,
  onCapture,
  captureLabel = "Capture Weight",
}) {
  const isLocalhost = useMemo(() => isLocalWeighbridgeHost(), []);
  const [liveStreaming, setLiveStreaming] = useState(isLocalhost);
  const [standbyIn, setStandbyIn] = useState(60);
  const live = useLiveScale();
  const { user } = useAuth();
  const canOverride = hasRole(user, ["admin", "manager"]);
  const [override, setOverride] = useState(false);
  const online = liveStreaming && live.agent_status === "ONLINE";
  const reconnecting = liveStreaming && live.link_status === "reconnecting";
  const connecting = liveStreaming && !online && !reconnecting && live.agent_status !== "OFFLINE";
  const blocked = isCloudScaleCaptureBlocked(live, { allowUnstable: canOverride && override });
  const kg = Number(live.weight_kg ?? live.weight);
  const qtl = kgToQtl(kg);
  const readout = liveStreaming && Number.isFinite(kg) && online ? formatKg(kg) : "---.-";

  useEffect(() => {
    if (!liveStreaming) return undefined;
    return acquireWeighbridgeStream();
  }, [liveStreaming]);

  useEffect(() => {
    if (!liveStreaming || isLocalhost) return undefined;
    let last = Date.now();
    const bump = () => {
      last = Date.now();
    };
    window.addEventListener("pointerdown", bump);
    window.addEventListener("keydown", bump);
    const id = window.setInterval(() => {
      const left = Math.max(0, IDLE_MS - (Date.now() - last));
      setStandbyIn(Math.ceil(left / 1000));
      if (left <= 0) setLiveStreaming(false);
    }, 500);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("pointerdown", bump);
      window.removeEventListener("keydown", bump);
    };
  }, [liveStreaming, isLocalhost]);

  return (
    <div
      className={`rounded-lg border border-border bg-card text-foreground p-4 space-y-3 ${className}`}
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Live weighbridge</p>
        <div className="flex flex-wrap items-center gap-1.5">
          {!liveStreaming ? <StatusPill label="STANDBY" tone="amber" /> : null}
          {liveStreaming && reconnecting ? <StatusPill label="RECONNECTING" tone="amber" /> : null}
          {liveStreaming && connecting ? <StatusPill label="CONNECTING" tone="amber" /> : null}
          {online ? <StatusPill label="ONLINE" tone="green" /> : null}
          {liveStreaming && !online && live.agent_status === "OFFLINE" ? (
            <StatusPill label="OFFLINE" tone="red" />
          ) : null}
          {online && live.is_stable ? <StatusPill label="STABLE" tone="blue" /> : null}
          {online && !live.is_stable ? <StatusPill label="UNSTABLE" tone="amber" /> : null}
        </div>
      </div>

      <div className="rounded-md border border-border bg-background px-3 py-4 text-center">
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mb-1">
          {liveStreaming && online ? "kg" : "QTL"}
        </p>
        <p
          className="font-mono text-4xl sm:text-5xl tabular-nums tracking-wider leading-none"
          data-testid={`${testId}-readout`}
        >
          {liveStreaming ? readout : "---.- QTL"}
        </p>
        {liveStreaming && online && qtl != null ? (
          <p className="mt-1 text-xs text-muted-foreground font-mono">{formatKg(qtl)} qtl</p>
        ) : null}
        <p className="mt-2 text-[10px] text-muted-foreground font-mono">{live.scale_id || "SCALE-01"}</p>
        {!liveStreaming ? (
          <p className="mt-2 text-xs text-muted-foreground">Scale Standby (Click to fetch live reading)</p>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-2">
        {!liveStreaming ? (
          <Button
            type="button"
            onClick={() => setLiveStreaming(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white min-h-[44px]"
            data-testid={`${testId}-connect`}
          >
            <Zap className="h-4 w-4 mr-1.5" />
            Connect & Fetch Live Scale
          </Button>
        ) : (
          <>
            <p className="text-[11px] text-muted-foreground flex-1">
              {isLocalhost
                ? "Live scale active on this mill PC"
                : `Live Scale Active (Auto-standby in ${standbyIn}s)`}
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => setLiveStreaming(false)}
              className="min-h-[40px] border-border"
              data-testid={`${testId}-disconnect`}
            >
              <Unplug className="h-4 w-4 mr-1.5" />
              Disconnect / Standby
            </Button>
          </>
        )}
      </div>

      {liveStreaming ? <ActiveOperators members={live.members} /> : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <PreviewFrame which="front" label="Truck front / plate" enabled={liveStreaming} />
        <PreviewFrame which="back" label="Truck bed" enabled={liveStreaming} />
      </div>

      {showCapture ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Button
            type="button"
            onClick={() => onCapture?.({ allowUnstable: canOverride && override })}
            disabled={capturing || blocked || !onCapture}
            className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-[44px]"
            data-testid={`${testId}-capture`}
          >
            <Radio className="h-4 w-4 mr-1.5" />
            {capturing ? "Capturing…" : captureLabel}
          </Button>
          {canOverride && online && !live.is_stable ? (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <input
                type="checkbox"
                className="accent-current"
                checked={override}
                onChange={(e) => setOverride(e.target.checked)}
              />
              Manager override (unstable)
            </label>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function ActiveOperators({ members }) {
  const list = Array.isArray(members) ? members : [];
  const active = list.filter((m) => m && m.active);
  if (active.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground">No weighbridge operators viewing this page.</p>
    );
  }
  return (
    <p className="text-[11px] text-muted-foreground" data-testid="weighbridge-group-active">
      On this page:{" "}
      <span className="text-foreground">
        {active.map((m) => m.name || m.email).filter(Boolean).join(", ")}
      </span>
    </p>
  );
}

function PreviewFrame({ which, label, enabled }) {
  const imgRef = useRef(null);
  const blobUrlRef = useRef("");
  const lastRawRef = useRef("");
  const lastAtRef = useRef(0);
  const [hasFrame, setHasFrame] = useState(false);
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setHasFrame(false);
      lastRawRef.current = "";
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = "";
      }
      return undefined;
    }
    const unsub = subscribeLivePreviews(({ front, back, at }) => {
      const raw = which === "front" ? front : back;
      const img = imgRef.current;
      if (at) {
        lastAtRef.current = at;
        setStale(false);
      }
      if (!raw || !img) return;
      if (raw === lastRawRef.current) return;
      lastRawRef.current = raw;
      try {
        const binary = atob(raw);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        const nextUrl = URL.createObjectURL(new Blob([bytes], { type: "image/jpeg" }));
        const prevUrl = blobUrlRef.current;
        blobUrlRef.current = nextUrl;
        img.src = nextUrl;
        if (prevUrl) URL.revokeObjectURL(prevUrl);
      } catch {
        img.src = b64Src(raw);
      }
      setHasFrame(true);
    });
    const tick = setInterval(() => {
      const seen = lastAtRef.current;
      setStale(Boolean(seen) && Date.now() - seen > 4000);
    }, 1000);
    return () => {
      unsub();
      clearInterval(tick);
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = "";
      }
    };
  }, [which, enabled]);

  return (
    <div className="rounded-md border border-border bg-card overflow-hidden">
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground px-2 py-1 border-b border-border">
        {label}
      </p>
      <div className="relative w-full aspect-video min-h-[240px] bg-muted">
        <img
          ref={imgRef}
          alt={label}
          className={`absolute inset-0 h-full w-full object-contain bg-background ${hasFrame && enabled ? "" : "hidden"}`}
        />
        {!enabled || !hasFrame ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center text-sm text-muted-foreground">
            <Camera className="h-8 w-8 text-foreground/40" />
            {enabled ? "Waiting for live camera…" : "Click “Connect & Fetch Live Scale” to view"}
          </div>
        ) : null}
        {enabled && hasFrame && stale ? (
          <div className="absolute inset-x-0 bottom-0 bg-amber-500/80 text-amber-950 text-[11px] font-medium text-center py-1">
            Camera stalled — waiting for a live frame
          </div>
        ) : null}
      </div>
    </div>
  );
}
